import { useId } from 'react';
import { Area, AreaChart } from 'recharts';
import { inkLight, secondaryBase } from '../../../styles/design-tokens';
import { Text } from '../../../ui-library/typography/typography';

type Props = {
  data?: number[] | null;
  width?: number;
  height?: number;
  color?: string;
};

/**
 * Minimal inline sparkline for table rows: per-period completed-task counts as
 * a soft area chart (the period size follows the tab's time-allocation filter).
 * Modeled on the audit-log Sparklines component but without its audit-specific
 * tooltip and total header.
 *
 * `data` is treated as optional even though the payload types it as required:
 * a single missing/null trend array must degrade to a one-cell em-dash rather
 * than throw and take down the whole table (there is no row-level boundary).
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The sparkline, or an em-dash when there is no signal
 */
export const TrendSparkline = ({ data, width = 90, height = 26, color = secondaryBase }: Props) => {
  // Unique per-instance gradient id so multiple sparklines on one page don't
  // collide; sanitized so the url(#id) reference always resolves.
  const gradientId = `model-trend-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const points = data ?? [];
  if (points.length === 0 || points.every((value) => value === 0)) {
    return (
      <Text size="small" color={inkLight}>
        —
      </Text>
    );
  }

  return (
    <AreaChart width={width} height={height} data={points.map((value) => ({ value }))}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.15} />
          <stop offset="100%" stopColor={color} stopOpacity={0.01} />
        </linearGradient>
      </defs>
      <Area
        type="linear"
        dataKey="value"
        stroke={color}
        strokeWidth={1.6}
        fill={`url(#${gradientId})`}
        fillOpacity={1}
        isAnimationActive={false}
        connectNulls
        activeDot={false}
      />
    </AreaChart>
  );
};
